import { useState, useEffect, useRef, useCallback } from 'react';
import ProtobufCollectionService from './ProtobufCollectionService';

const useProtobufCollection = (statusUuid, recordUuid, protobufType, protobufRoot, protobufProtocolRoot, protobufProtocolService) => {
  const [collectionStatus, setCollectionStatus] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [collectionLoaded, setCollectionLoaded] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0, loading: false });
  const [initialized, setInitialized] = useState(false); // <-- add this line
  
  const serviceRef = useRef(null);
  // eslint-disable-next-line no-unused-vars -- AUD-DEP-13-LINT: pre-existing write-only/unused binding in the legacy BLE+compass tree. Removing it is a behaviour question, not a lint fix; suppressed explicitly so no-unused-vars can stay an ERROR everywhere else.
  const recordsRef = useRef([]);

  // Initialize the service
  useEffect(() => {
    if (!statusUuid || !recordUuid || !protobufType || !protobufRoot || !protobufProtocolRoot || !protobufProtocolService) {
      setInitialized(false); // <-- reset if not ready
      return;
    }

    const initializeService = async () => {
      try {
        setLoading(true);
        setError(null);

        const service = new ProtobufCollectionService(
          statusUuid,
          recordUuid,
          protobufType,
          protobufRoot,
          protobufProtocolRoot,
          protobufProtocolService
        );

        // Set up event handlers
        service.onStatusChange = (status) => {
          setCollectionStatus(status);
        };

        service.onRecordChange = (index, recordData) => {
          // Guard against invalid indices
          if (typeof index !== 'number' || !Number.isInteger(index) || index < 0) {
            console.warn('Ignoring record with invalid index:', index, recordData);
            return prevRecords => prevRecords; // No change
          }
          setRecords(prevRecords => {
            const newRecords = [...prevRecords];
            // Ensure array is large enough
            while (newRecords.length <= index) {
              newRecords.push(null);
            }
            newRecords[index] = recordData;
            return newRecords;
          });
        };

        service.onError = (errorMessage) => {
          setError(errorMessage);
        };

        // Initialize the service
        const success = await service.initialize();
        if (success) {
          serviceRef.current = service;
          setInitialized(true); // <-- set initialized true
          console.log('Collection service initialized');
        } else {
          setInitialized(false); // <-- set initialized false
          setError('Failed to initialize collection service');
        }
      } catch (err) {
        setInitialized(false); // <-- set initialized false
        console.error('Error initializing collection service:', err);
        setError(`Failed to initialize collection service: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    initializeService();

    // Cleanup function
    return () => {
      if (serviceRef.current) {
        serviceRef.current.disconnect();
        serviceRef.current = null;
      }
      setInitialized(false); // <-- reset on cleanup
    };
  }, [statusUuid, recordUuid, protobufType, protobufRoot, protobufProtocolRoot, protobufProtocolService]);

  // Explicitly expose loadCollectionStatus for use in the table
  const loadCollectionStatus = useCallback(async () => {
    if (!serviceRef.current){ 
      console.error('No collection service available');
      return;
    }
    console.log('loadCollectionStatus called');
    setLoading(true);
    setError(null);
    try {
      await serviceRef.current.loadCollectionStatus();
    } catch (err) {
      setError(`Failed to load collection status: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const clearCollection = useCallback(async () => {
    if (!serviceRef.current) return;
    
    try {
      setLoading(true);
      setError(null);
      
      await serviceRef.current.clearCollection();
      setRecords([]);
      setCollectionLoaded(false);
      
    } catch (err) {
      setError(`Failed to clear collection: ${err.message}`);
      console.error('Error clearing collection:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCollection = useCallback(async (onProgress) => {
    if (!serviceRef.current) {
      console.error('No collection service available');
      return;
    }
    
    // Debug logging to see what's being passed
    console.log('loadCollection called with onProgress:', onProgress, 'type:', typeof onProgress);
    
    // Provide a default onProgress handler if none is provided
    const fallbackOnProgress = (typeof onProgress === 'function') ? onProgress : (() => {});
    
    try {
      setLoading(true);
      setError(null);
      
      // Always load the latest collection status before proceeding
      const latestStatus = await serviceRef.current.loadCollectionStatus();
      if (!latestStatus || latestStatus.currentRecords === 0) {
        console.log('No records found, status:', latestStatus);
        setCollectionLoaded(true);
        setLoadingProgress({ current: 0, total: 0, loading: false });
        fallbackOnProgress({ current: 0, total: 0, loading: false });
        return;
      }
      // Use latestStatus for the rest of the function
      setRecords([]);
      const totalRecords = latestStatus.currentRecords;
      setLoadingProgress({ current: 0, total: totalRecords, loading: true });
      fallbackOnProgress({ current: 0, total: totalRecords, loading: true });
      
      // Load the collection with progress tracking
      await serviceRef.current.loadCollection(
        latestStatus,
        (progress) => {
          setLoadingProgress({ ...progress });
          fallbackOnProgress(progress);
        }
      );
      
      // Mark collection as loaded - records were added via onRecordChange
      setCollectionLoaded(true);
      
    } catch (err) {
      console.error('Error loading collection:', err);
      setError(`Failed to load collection: ${err.message}`);
      setLoadingProgress({ current: 0, total: 0, loading: false });
      fallbackOnProgress({ current: 0, total: 0, loading: false });
    } finally {
      setLoading(false);
    }
  }, []);

  const addItem = useCallback(async () => {
    if (!serviceRef.current) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const newRecord = await serviceRef.current.addItem();
      
      // Update local state directly instead of reloading
      setRecords(prevRecords => [...prevRecords, newRecord]);
      
      // Update local collection status
      setCollectionStatus(prevStatus => ({
        ...prevStatus,
        currentRecords: (prevStatus?.currentRecords || 0) + 1
      }));
      
    } catch (err) {
      setError(`Failed to add item: ${err.message}`);
      console.error('Error adding item:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteLastItem = useCallback(async () => {
    if (!serviceRef.current || records.length === 0) return;
    
    try {
      setLoading(true);
      setError(null);
      
      await serviceRef.current.deleteLastItem(records.length);
      
      // Update local state directly instead of reloading
      setRecords(prevRecords => prevRecords.slice(0, -1));
      
      // Update local collection status
      setCollectionStatus(prevStatus => ({
        ...prevStatus,
        currentRecords: records.length - 1
      }));
      
    } catch (err) {
      setError(`Failed to delete last item: ${err.message}`);
      console.error('Error deleting last item:', err);
    } finally {
      setLoading(false);
    }
  }, [records.length]);

  const updateRecord = useCallback(async (index, updatedRecord) => {
    if (!serviceRef.current) return;
    
    try {
      console.log('useProtobufCollection.js: updateRecord called with index:', index, 'updatedRecord:', updatedRecord);
      
      await serviceRef.current.updateRecord(index, updatedRecord);
      
      console.log('Successfully sent record update for index:', index);
      
      // Update local state
      setRecords(prevRecords => {
        const newRecords = [...prevRecords];
        newRecords[index] = updatedRecord;
        return newRecords;
      });
      
    } catch (err) {
      console.error('Error updating record for index:', index, 'error:', err);
      setError(`Failed to update record: ${err.message}`);
    }
  }, []);

  const finishTransaction = useCallback(async (totalRecords) => {
    if (!serviceRef.current) return;
    
    try {
      setLoading(true);
      setError(null);
      
      await serviceRef.current.finishTransaction(totalRecords);
      
    } catch (err) {
      setError(`Failed to finish transaction: ${err.message}`);
      console.error('Error finishing transaction:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const uploadCollection = useCallback(async (records, onProgress, finishTransaction) => {
    if (!serviceRef.current) {
      throw new Error('No collection service available');
    }
    // Provide a default onProgress handler if none is provided
    const fallbackOnProgress = (typeof onProgress === 'function') ? onProgress : (() => {});
    
    try {
      setLoading(true);
      setError(null);
      
      // Clear existing records before upload
      setRecords([]);
      setCollectionLoaded(false);
      
      // Upload the collection with progress tracking
      await serviceRef.current.uploadCollection(records, (progress) => {
        fallbackOnProgress(progress);
      }, finishTransaction);
      
      // Reload the collection to get the uploaded records, but without progress callback
      // to avoid changing the total value after upload completes
      //await loadCollection();
      
    } catch (err) {
      console.error('Error uploading collection:', err);
      setError(`Failed to upload collection: ${err.message}`);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadCollection]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // State
    collectionStatus,
    records,
    loading,
    error,
    collectionLoaded,
    loadingProgress,
    initialized, // <-- add this line
    // Actions
    clearCollection,
    loadCollection,
    addItem,
    deleteLastItem,
    updateRecord,
    finishTransaction,
    uploadCollection,
    clearError,
    setLoadingProgress,
    loadCollectionStatus
  };
};

export default useProtobufCollection; 